import { FC, useEffect, useRef } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { useSearchParams } from 'react-router-dom';

import { AgencyCouponSettlementSection } from '../components/giftSettlement/AgencyCouponSettlementSection';
import { AgencyDeliverySettlementSection } from '../components/giftSettlement/AgencyDeliverySettlementSection';
import { AgencyUnifiedMonthlySection } from '../components/giftSettlement/AgencyUnifiedMonthlySection';
import { useAdminUnifiedSettlementData } from '../hooks/useAdminUnifiedSettlementData';

// ─────────────────────────────────────────────
// 상조 답례품 정산 관리 (통합)
//
// 배송형·쿠폰형이 따로 있어 "이번 달 상조별로 얼마인지"를 한눈에 못 보던 문제 해결
// (2026-07-31). 정산 문서는 백엔드에서 종류별로 분리 유지하고, 화면에서만
// - '월별 합계' 탭: 정산월(×상조) 기준으로 총금액을 합산해 보여주고
// - '배송형'·'쿠폰형' 탭: 기존 집행·수정·확정 화면을 그대로 마운트한다.
// 탭은 ?tab= 쿼리로 유지 — 사이드바에서 바로 해당 탭으로 진입한다.
// ─────────────────────────────────────────────

type SettlementTab = 'summary' | 'delivery' | 'coupon';

const TABS: { key: SettlementTab; label: string }[] = [
  { key: 'summary', label: '월별 합계' },
  { key: 'delivery', label: '배송형' },
  { key: 'coupon', label: '쿠폰형' },
];

const isTab = (v: string | null): v is SettlementTab =>
  v === 'summary' || v === 'delivery' || v === 'coupon';

export const GiftSettlementAgencyPage: FC = function GiftSettlementAgencyPage() {
  const { isAdmin, isGiftAgent } = useAuth();
  const canWrite = isAdmin() && !isGiftAgent();
  // GiftAgent(마리) 조회 뷰: viaMari 상조 건만 + 쓰기 UI 숨김
  const giftAgentView = !isAdmin() && isGiftAgent();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: SettlementTab = isTab(tabParam) ? tabParam : 'summary';

  const setTab = (next: SettlementTab) =>
    setSearchParams(next === 'summary' ? {} : { tab: next }, { replace: true });

  const { rebateItems, rebateLoading, couponItems, couponLoading, refresh } =
    useAdminUnifiedSettlementData(giftAgentView);

  // 다른 탭에서 집행·확정하고 돌아오면 합계가 옛 값이면 안 되므로 재조회한다
  // (첫 마운트는 훅이 이미 부르므로 건너뛴다)
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (tab === 'summary') refresh();
  }, [tab, refresh]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">상조 답례품 정산 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            배송형·쿠폰형 정산을 한 화면에서 조회{canWrite ? ' · 집행 · 수정 · 확정' : ''}
          </p>
        </div>

        {/* 탭 */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-0">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`w-36 border-b-2 px-5 py-2.5 text-center text-sm font-medium transition-colors ${
                  tab === t.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'summary' && (
          <AgencyUnifiedMonthlySection
            rebateItems={rebateItems}
            couponItems={couponItems}
            loading={rebateLoading || couponLoading}
            giftAgentView={giftAgentView}
            onOpenDelivery={() => setTab('delivery')}
            onOpenCoupon={() => setTab('coupon')}
          />
        )}

        {tab === 'delivery' && (
          <AgencyDeliverySettlementSection onSettlementChanged={refresh} />
        )}

        {tab === 'coupon' && (
          <AgencyCouponSettlementSection onSettlementChanged={refresh} />
        )}
      </div>
    </div>
  );
};
